import * as Phaser from 'phaser';
import type { RaceState } from '../sim/race.ts';
import {
  BADGE_FILL_ALPHA,
  BADGE_FILL_COLOR,
  BADGE_GOAL_STROKE,
  BADGE_RADIUS_PX,
  BADGE_STROKE_ALPHA,
  BADGE_TEXT_COLOR,
  DEGENERATE_GATE_WIDTH,
  GATE_CHECKER_ALPHA,
  GATE_CHECKER_DARK,
  GATE_CHECKER_LIGHT,
  GATE_CHECKER_SELECTED_ALPHA,
  GATE_CHECKER_SIZE_PX,
  GATE_CHECKER_WIDTH_PX,
  GATE_DASH_PX,
  GATE_GAP_PX,
  GATE_LINE_ALPHA,
  GATE_LINE_COLOR,
  GATE_LINE_SELECTED_ALPHA,
  GATE_LINE_WIDTH_PX,
  POLE_PASSED_COLOR,
  POLE_PENDING_COLOR,
  POLE_PREVIEW_COLOR,
  POLE_RADIUS_WORLD,
  POLE_SELECTED_STROKE,
  POLE_SELECTED_STROKE_PX,
  POLE_STUB_FADE_SLICES,
  POLE_SUBMERGED_WORLD,
  CONTACT_BLOB_MIX,
  contactBlobAxes,
  checkeredCells,
  checkpointBadgePosition,
  checkpointDisplayOrder,
  dashedSegments,
  distance,
  poleCap,
  poleWorldScale,
  poleStubFade,
  poleStubVisibleSamples,
  waterSpans,
  ySortDepth,
  type Checkpoint,
  type CheckpointFlag,
  type DashSegment,
  type MapDocument,
  type Vec2,
} from '../sim/map/index.ts';
import { pointInSolid } from '../sim/collision.ts';
import { MeshPaintSink, type PaintSink } from '../sim/map/index.ts';
import { mixColor } from './colorMix.ts';
import { StaticMesh } from './terrain/staticMesh.ts';
import { earcutTriangulate } from './terrain/triangulate.ts';

/**
 * A gate layer is a Graphics (replayed every frame) or a `StaticMesh` (uploaded once per
 * `drawCheckpoints`). The painters below only see a `PaintSink`. See docs/PERFORMANCE.md.
 */
export type CheckpointLayer = Phaser.GameObjects.Graphics | StaticMesh;

export type CheckpointGraphics = {
  above: CheckpointLayer;
  /** Dash / checkered waterline — on the water, under the hull. */
  waterline: CheckpointLayer;
  submerged: CheckpointLayer;
};

type CheckpointSinks = {
  above: PaintSink;
  waterline: PaintSink;
  submerged: PaintSink;
};

type LayerPainter = { sink: PaintSink; commit: () => void };

/** Clear a layer and hand back where to paint; `commit` uploads a mesh layer. */
function beginLayer(layer: CheckpointLayer): LayerPainter {
  if (layer instanceof StaticMesh) {
    const sink = new MeshPaintSink(earcutTriangulate);
    return { sink, commit: () => layer.setGeometry(sink.finish()) };
  }

  layer.clear();
  return { sink: layer, commit: () => undefined };
}

export type DrawCheckpointsOptions = {
  selectedIds?: ReadonlySet<string>;
  /** When set, outlines only these poles. The gate line highlights if either pole is listed. */
  selectedPoles?: ReadonlyMap<string, readonly CheckpointFlag[]>;
  /** Pending gates the planned move would cross — poles draw yellow. */
  previewIds?: ReadonlySet<string>;
  /** Reuses water-clipped geometry across repaints. Omit while the map is being edited. */
  geometry?: CheckpointGeometryCache;
  /**
   * Per-shaft Graphics so a south prop can cover a north pole. One shared
   * `above` layer cannot interleave with island overlays.
   */
  shafts?: CheckpointPolePool;
  /** Rubber-band second flag; shafts join the same Y-sort band as committed poles. */
  draft?: { a: Vec2; b: Vec2 };
};

/**
 * One object per shaft / badge so props and poles share `ySortDepth`. With
 * `staticGeometry` each shaft is a `StaticMesh` uploaded when `commit()` runs at the
 * end of `drawCheckpoints`, instead of a Graphics that re-triangulates its cap circles
 * every frame.
 */
export class CheckpointPolePool {
  private readonly objects = new Map<string, CheckpointLayer>();
  private readonly pending: LayerPainter[] = [];
  private readonly scene: Phaser.Scene;
  private readonly ySort: { base: number; span: number };
  private readonly staticGeometry: boolean;

  constructor(
    scene: Phaser.Scene,
    ySort: { base: number; span: number },
    options: { staticGeometry?: boolean } = {},
  ) {
    this.scene = scene;
    this.ySort = ySort;
    this.staticGeometry = options.staticGeometry === true && StaticMesh.isSupported(scene);
  }

  acquire(key: string, y: number, worldHeight: number): PaintSink {
    let layer = this.objects.get(key);

    if (!layer || !layer.scene) {
      layer = this.staticGeometry
        ? this.scene.add.existing(new StaticMesh(this.scene))
        : this.scene.add.graphics();
      this.objects.set(key, layer);
    }

    layer.setDepth(ySortDepth(this.ySort.base, y, worldHeight, this.ySort.span));
    const painter = beginLayer(layer);
    this.pending.push(painter);
    return painter.sink;
  }

  /** Upload every shaft acquired since the last commit. */
  commit(): void {
    for (const painter of this.pending) {
      painter.commit();
    }

    this.pending.length = 0;
  }

  graphics(): CheckpointLayer[] {
    return [...this.objects.values()];
  }

  prune(seen: Set<string>): void {
    for (const [key, layer] of this.objects) {
      if (seen.has(key)) {
        continue;
      }

      layer.destroy();
      this.objects.delete(key);
    }
  }

  destroy(): void {
    for (const layer of this.objects.values()) {
      layer.destroy();
    }

    this.objects.clear();
    this.pending.length = 0;
  }
}

/**
 * The part of a gate that only the map can change: the runs of the gate line that sit on open
 * water, and how deep each stub reaches before it hits ground. Colour and zoom do not affect it.
 */
type GateGeometry = {
  water: DashSegment[];
  /** 0 when the pole stands on land and has no underwater part. */
  stubA: number;
  stubB: number;
  /** False for a point gate, whose two flags share one spot. */
  wide: boolean;
};

function selectedPolesFor(
  checkpointId: string,
  options: DrawCheckpointsOptions,
): { a: boolean; b: boolean; line: boolean } {
  const poles = options.selectedPoles?.get(checkpointId);

  if (poles) {
    const a = poles.includes('a');
    const b = poles.includes('b');
    return { a, b, line: a || b };
  }

  const selected = options.selectedIds?.has(checkpointId) === true;
  return { a: selected, b: selected, line: selected };
}

function poleColor(
  checkpoint: Checkpoint,
  race: RaceState | null,
  previewIds?: ReadonlySet<string>,
): number {
  if (race?.passedCheckpointIds.has(checkpoint.id)) {
    return POLE_PASSED_COLOR;
  }

  if (previewIds?.has(checkpoint.id)) {
    return POLE_PREVIEW_COLOR;
  }

  return POLE_PENDING_COLOR;
}

function landAt(map: MapDocument): (point: Vec2) => boolean {
  return (point) => pointInSolid(map, point);
}

function stubExtent(map: MapDocument, base: Vec2): number {
  return poleStubVisibleSamples(base, landAt(map)).at(-1)?.t ?? 0;
}

/**
 * Clipping a gate to open water samples `pointInSolid` along its whole length, and every one of
 * those samples re-tessellates every island and rock — tens of milliseconds on a real map. The
 * dash pattern is laid inside the water runs at paint time so this survives a zoom change too.
 */
function computeGateGeometry(map: MapDocument, a: Vec2, b: Vec2): GateGeometry {
  const wide = distance(a, b) > DEGENERATE_GATE_WIDTH;
  const isLand = landAt(map);

  return {
    water: wide ? waterSpans(a, b, isLand) : [],
    stubA: stubExtent(map, a),
    stubB: wide ? stubExtent(map, b) : 0,
    wide,
  };
}

export class CheckpointGeometryCache {
  private map: MapDocument | null = null;
  private readonly gates = new Map<string, GateGeometry>();

  clear(): void {
    this.map = null;
    this.gates.clear();
  }

  gate(map: MapDocument, checkpoint: Checkpoint): GateGeometry {
    if (map !== this.map) {
      this.map = map;
      this.gates.clear();
    }

    const cached = this.gates.get(checkpoint.id);

    if (cached) {
      return cached;
    }

    const geometry = computeGateGeometry(map, checkpoint.a, checkpoint.b);
    this.gates.set(checkpoint.id, geometry);
    return geometry;
  }
}

function strokeDashes(
  gfx: PaintSink,
  water: readonly DashSegment[],
  strokeScale: number,
  selected: boolean,
): void {
  if (water.length === 0) {
    return;
  }

  const width = GATE_LINE_WIDTH_PX * strokeScale * (selected ? 1.5 : 1);
  const alpha = selected ? GATE_LINE_SELECTED_ALPHA : GATE_LINE_ALPHA;
  gfx.lineStyle(width, GATE_LINE_COLOR, alpha);

  for (const span of water) {
    for (const dash of dashedSegments(
      span.a,
      span.b,
      GATE_DASH_PX * strokeScale,
      GATE_GAP_PX * strokeScale,
    )) {
      gfx.lineBetween(dash.a.x, dash.a.y, dash.b.x, dash.b.y);
    }
  }
}

function chromeZoom(strokeScale: number): number {
  return strokeScale > 0 ? 1 / strokeScale : 1;
}

function fillCheckers(
  gfx: PaintSink,
  water: readonly DashSegment[],
  strokeScale: number,
  selected: boolean,
): void {
  if (water.length === 0) {
    return;
  }

  const scale = poleWorldScale(chromeZoom(strokeScale)) * (selected ? 1.5 : 1);
  const cell = GATE_CHECKER_SIZE_PX * scale;
  const halfWidth = (GATE_CHECKER_WIDTH_PX * scale) / 2;
  const alpha = selected ? GATE_CHECKER_SELECTED_ALPHA : GATE_CHECKER_ALPHA;

  for (const span of water) {
    for (const quad of checkeredCells(span.a, span.b, cell, halfWidth)) {
      gfx.fillStyle(quad.dark ? GATE_CHECKER_DARK : GATE_CHECKER_LIGHT, alpha);
      gfx.fillTriangle(quad.a0.x, quad.a0.y, quad.a1.x, quad.a1.y, quad.b1.x, quad.b1.y);
      gfx.fillTriangle(quad.a0.x, quad.a0.y, quad.b1.x, quad.b1.y, quad.b0.x, quad.b0.y);
    }
  }
}

function drawPoleAbove(
  gfx: PaintSink,
  base: Vec2,
  color: number,
  selected: boolean,
  strokeScale: number,
): void {
  const scale = poleWorldScale(chromeZoom(strokeScale));
  const cap = poleCap(base, scale);
  const radius = POLE_RADIUS_WORLD * scale;
  const capRadius = radius * 0.85;
  const shaft = mixColor(color, 0x1a0a0a, 0.22);
  const waterWidth = radius * 2;
  const blob = contactBlobAxes(waterWidth);

  gfx.fillStyle(shaft, 1);
  gfx.fillTriangle(
    base.x - radius,
    base.y,
    base.x + radius,
    base.y,
    cap.x + capRadius,
    cap.y,
  );
  gfx.fillTriangle(
    base.x - radius,
    base.y,
    cap.x + capRadius,
    cap.y,
    cap.x - capRadius,
    cap.y,
  );

  gfx.fillStyle(mixColor(color, 0x000000, CONTACT_BLOB_MIX), 1);
  gfx.fillEllipse(base.x, base.y, blob.width, blob.height);

  gfx.fillStyle(color, 1);
  gfx.fillCircle(cap.x, cap.y, capRadius);
  gfx.fillStyle(0xffffff, 0.28);
  gfx.fillCircle(cap.x - capRadius * 0.28, cap.y - capRadius * 0.32, capRadius * 0.32);

  if (selected) {
    gfx.lineStyle(POLE_SELECTED_STROKE_PX * strokeScale, POLE_SELECTED_STROKE, 1);
    gfx.strokeCircle(cap.x, cap.y, capRadius);
    gfx.lineBetween(base.x - radius, base.y, cap.x - capRadius, cap.y);
    gfx.lineBetween(base.x + radius, base.y, cap.x + capRadius, cap.y);
  }
}

function drawPoleStub(
  gfx: PaintSink,
  base: Vec2,
  color: number,
  extent: number,
  strokeScale: number,
): void {
  if (extent <= 0) {
    return;
  }

  const radius = POLE_RADIUS_WORLD * poleWorldScale(chromeZoom(strokeScale));
  const shaft = mixColor(color, 0x1a0a0a, 0.22);

  for (let slice = 0; slice < POLE_STUB_FADE_SLICES; slice += 1) {
    const t0 = extent * (slice / POLE_STUB_FADE_SLICES);
    const t1 = extent * ((slice + 1) / POLE_STUB_FADE_SLICES);
    const fade = poleStubFade((t0 + t1) / 2);

    if (fade <= 0) {
      continue;
    }

    fillStubShaft(
      gfx,
      base.x,
      base.y + POLE_SUBMERGED_WORLD * t0,
      base.y + POLE_SUBMERGED_WORLD * t1,
      radius,
      shaft,
      fade,
    );
  }
}

function fillStubShaft(
  gfx: PaintSink,
  x: number,
  fromY: number,
  toY: number,
  radius: number,
  color: number,
  alpha: number,
): void {
  if (toY <= fromY) {
    return;
  }

  gfx.fillStyle(color, alpha);
  gfx.fillTriangle(x - radius, fromY, x + radius, fromY, x + radius, toY);
  gfx.fillTriangle(x - radius, fromY, x + radius, toY, x - radius, toY);
}

function drawBadgeCircle(
  gfx: PaintSink,
  position: Vec2,
  isGoal: boolean,
  strokeScale: number,
): void {
  const radius = BADGE_RADIUS_PX * strokeScale;
  gfx.fillStyle(BADGE_FILL_COLOR, BADGE_FILL_ALPHA);
  gfx.fillCircle(position.x, position.y, radius);
  gfx.lineStyle(
    (isGoal ? 2 : 1.25) * strokeScale,
    isGoal ? BADGE_GOAL_STROKE : GATE_LINE_COLOR,
    isGoal ? 0.9 : BADGE_STROKE_ALPHA,
  );
  gfx.strokeCircle(position.x, position.y, radius);
}

function drawGateWaterAndStubs(
  graphics: CheckpointSinks,
  geometry: GateGeometry,
  a: Vec2,
  b: Vec2,
  color: number,
  strokeScale: number,
  selectedLine: boolean,
  isGoal: boolean,
  /** Passed gates keep poles but drop the dashed / checkered waterline. */
  hideLine = false,
): void {
  if (!hideLine) {
    if (isGoal && geometry.wide) {
      fillCheckers(graphics.waterline, geometry.water, strokeScale, selectedLine);
    } else {
      strokeDashes(graphics.waterline, geometry.water, strokeScale, selectedLine);
    }
  }

  drawPoleStub(graphics.submerged, a, color, geometry.stubA, strokeScale);

  if (geometry.wide) {
    drawPoleStub(graphics.submerged, b, color, geometry.stubB, strokeScale);
  }
}

function takeShaft(
  shafts: CheckpointPolePool,
  seen: Set<string>,
  key: string,
  y: number,
  worldHeight: number,
): PaintSink {
  seen.add(key);
  return shafts.acquire(key, y, worldHeight);
}

function paintGateShafts(
  shafts: CheckpointPolePool,
  seen: Set<string>,
  worldHeight: number,
  id: string,
  geometry: GateGeometry,
  a: Vec2,
  b: Vec2,
  color: number,
  strokeScale: number,
  selectedA: boolean,
  selectedB: boolean,
  isGoal: boolean,
  showBadge: boolean,
): void {
  drawPoleAbove(takeShaft(shafts, seen, `${id}:a`, a.y, worldHeight), a, color, selectedA, strokeScale);

  if (geometry.wide) {
    drawPoleAbove(
      takeShaft(shafts, seen, `${id}:b`, b.y, worldHeight),
      b,
      color,
      selectedB,
      strokeScale,
    );
  }

  if (!showBadge) {
    return;
  }

  const position = checkpointBadgePosition(a, b, strokeScale);
  drawBadgeCircle(
    takeShaft(shafts, seen, `${id}:badge`, position.y, worldHeight),
    position,
    isGoal,
    strokeScale,
  );
}

function paintGateShaftsShared(
  gfx: PaintSink,
  geometry: GateGeometry,
  a: Vec2,
  b: Vec2,
  color: number,
  strokeScale: number,
  selectedA: boolean,
  selectedB: boolean,
  isGoal: boolean,
  showBadge: boolean,
): void {
  drawPoleAbove(gfx, a, color, selectedA, strokeScale);

  if (geometry.wide) {
    drawPoleAbove(gfx, b, color, selectedB, strokeScale);
  }

  if (showBadge) {
    drawBadgeCircle(gfx, checkpointBadgePosition(a, b, strokeScale), isGoal, strokeScale);
  }
}

/** Redrawn after every move so pole colour reflects whether the gate has been passed. */
export function drawCheckpoints(
  graphics: CheckpointGraphics,
  map: MapDocument,
  race: RaceState | null,
  strokeScale: number,
  options: DrawCheckpointsOptions = {},
): void {
  const above = beginLayer(graphics.above);
  const waterline = beginLayer(graphics.waterline);
  const submerged = beginLayer(graphics.submerged);
  const sinks: CheckpointSinks = {
    above: above.sink,
    waterline: waterline.sink,
    submerged: submerged.sink,
  };

  const worldHeight = map.bounds.height;
  const seen = new Set<string>();

  for (const checkpoint of map.checkpoints) {
    const selected = selectedPolesFor(checkpoint.id, options);
    const geometry =
      options.geometry?.gate(map, checkpoint) ??
      computeGateGeometry(map, checkpoint.a, checkpoint.b);
    const color = poleColor(checkpoint, race, options.previewIds);
    const passed = race?.passedCheckpointIds.has(checkpoint.id) === true;
    const showBadge = !passed && checkpointDisplayOrder(checkpoint) !== null;
    drawGateWaterAndStubs(
      sinks,
      geometry,
      checkpoint.a,
      checkpoint.b,
      color,
      strokeScale,
      selected.line,
      checkpoint.isGoal,
      passed,
    );

    if (options.shafts) {
      paintGateShafts(
        options.shafts,
        seen,
        worldHeight,
        checkpoint.id,
        geometry,
        checkpoint.a,
        checkpoint.b,
        color,
        strokeScale,
        selected.a,
        selected.b,
        checkpoint.isGoal,
        showBadge,
      );
    } else {
      paintGateShaftsShared(
        sinks.above,
        geometry,
        checkpoint.a,
        checkpoint.b,
        color,
        strokeScale,
        selected.a,
        selected.b,
        checkpoint.isGoal,
        showBadge,
      );
    }
  }

  if (options.draft) {
    const geometry = computeGateGeometry(map, options.draft.a, options.draft.b);
    drawGateWaterAndStubs(
      sinks,
      geometry,
      options.draft.a,
      options.draft.b,
      POLE_PENDING_COLOR,
      strokeScale,
      false,
      false,
    );

    if (options.shafts) {
      paintGateShafts(
        options.shafts,
        seen,
        worldHeight,
        'draft',
        geometry,
        options.draft.a,
        options.draft.b,
        POLE_PENDING_COLOR,
        strokeScale,
        false,
        false,
        false,
        false,
      );
    } else {
      paintGateShaftsShared(
        sinks.above,
        geometry,
        options.draft.a,
        options.draft.b,
        POLE_PENDING_COLOR,
        strokeScale,
        false,
        false,
        false,
        false,
      );
    }
  }

  above.commit();
  waterline.commit();
  submerged.commit();
  options.shafts?.commit();
  options.shafts?.prune(seen);
}

/** False after a scene shutdown: the Text is still cached but no longer on a display list. */
function isLiveLabel(label: Phaser.GameObjects.Text | undefined): label is Phaser.GameObjects.Text {
  return label !== undefined && label.scene !== undefined && label.scene !== null;
}

export function syncCheckpointBadges(
  scene: Phaser.Scene,
  labels: Map<string, Phaser.GameObjects.Text>,
  map: MapDocument,
  strokeScale: number,
  depth: number | { base: number; span: number },
  options: { hideIds?: ReadonlySet<string> } = {},
): void {
  const seen = new Set<string>();

  for (const checkpoint of map.checkpoints) {
    seen.add(checkpoint.id);

    const order = checkpointDisplayOrder(checkpoint);
    let label = labels.get(checkpoint.id);

    if (label && !isLiveLabel(label)) {
      labels.delete(checkpoint.id);
      label = undefined;
    }

    if (order === null || options.hideIds?.has(checkpoint.id)) {
      if (label) {
        label.destroy();
        labels.delete(checkpoint.id);
      }
      continue;
    }

    if (!label) {
      label = scene.add.text(0, 0, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: BADGE_TEXT_COLOR,
        align: 'center',
      });
      label.setOrigin(0.5);
      labels.set(checkpoint.id, label);
    }

    const position = checkpointBadgePosition(checkpoint.a, checkpoint.b, strokeScale);
    const labelDepth =
      typeof depth === 'number'
        ? depth
        : ySortDepth(depth.base, position.y, map.bounds.height, depth.span) + 1e-6;
    label.setText(String(order));
    label.setPosition(position.x, position.y);
    label.setScale(strokeScale);
    label.setDepth(labelDepth);
    label.setVisible(true);
  }

  for (const [id, label] of labels) {
    if (!seen.has(id) || !isLiveLabel(label)) {
      if (isLiveLabel(label)) {
        label.destroy();
      }
      labels.delete(id);
    }
  }

  const parent = scene.game.canvas.parentElement;
  const texts: string[] = [];

  for (const label of labels.values()) {
    if (isLiveLabel(label) && label.visible && label.text !== '') {
      texts.push(label.text);
    }
  }

  parent?.setAttribute('data-checkpoint-badges', texts.join(','));
}
